// CHG-130 REQ-DHR-022: 일반 회의록 생성 모달
import { useState, useEffect, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateGeneralMeeting } from '../../hooks/useGeneralMeetings';
import type { ProjectDetail, ProjectMember } from '../../services/projectService';
import Spinner from '../ui/Spinner';

interface Props {
  open: boolean;
  onClose: () => void;
  project: ProjectDetail;
  spaceId: string;
  generalMeetingFolderId: string;
  onCreated: (newDocId: string) => void;
}

function todayKstString(): string {
  // 클라이언트 측 KST 'YYYY-MM-DD' (Asia/Seoul)
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export default function CreateGeneralMeetingModal({
  open, onClose, project, spaceId, generalMeetingFolderId, onCreated,
}: Props) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(todayKstString());
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);

  const createMutation = useCreateGeneralMeeting(project.id);

  // 모달 닫힘 시 상태 초기화
  useEffect(() => {
    if (!open) {
      setTitle('');
      setMeetingDate(todayKstString());
      setAttendeeIds([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const attendeesByMember = useMemo(() => new Set(attendeeIds), [attendeeIds]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || createMutation.isPending) return;
    try {
      const attendeeNames = project.members
        .filter((m) => attendeesByMember.has(m.userId))
        .map((m) => m.user.name);
      // 본문 빈 상태로 생성 (REQ-DHR-023: 본 CHG는 본문 자동 시드 변경 없음)
      // 참석자 정보는 본문 시드 텍스트로 첫 줄 삽입 — Tiptap JSON 기본 빈 doc 형식
      const content = [
        {
          type: 'paragraph',
          content: attendeeNames.length > 0
            ? [{ type: 'text', text: `참석자: ${attendeeNames.join(', ')}` }]
            : [],
        },
      ];
      const created = await createMutation.mutateAsync({
        spaceId,
        parentDocumentId: generalMeetingFolderId,
        title: title.trim(),
        scrumDate: meetingDate,
        content,
      });
      toast.success('일반 회의록이 생성되었습니다.');
      onCreated(created.id);
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ?? '회의록 생성에 실패했습니다.';
      toast.error(msg);
    }
  };

  const toggleAttendee = (userId: string) => {
    setAttendeeIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-general-meeting-title"
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 id="create-general-meeting-title" className="text-lg font-semibold text-gray-900">일반 회의록 생성</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" aria-label="닫기">
            <X className="size-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 4월 스프린트 회고"
              maxLength={500}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">회의 날짜</label>
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">참석자 (선택)</label>
            {project.members.length === 0 ? (
              <p className="text-sm text-gray-400">멤버가 없습니다.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {project.members.map((m: ProjectMember) => {
                  const checked = attendeesByMember.has(m.userId);
                  return (
                    <label key={m.userId} className="flex items-center gap-2 px-3 py-2 hover:bg-primary-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAttendee(m.userId)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-900 flex-1 truncate">{m.user.name}</span>
                      <span className="text-sm text-gray-400">{m.projectRole}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-sm text-gray-400 mt-1">선택 {attendeeIds.length}명 · STAKEHOLDER도 참석자 표시 가능</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              취소
            </button>
            <button type="submit" disabled={!title.trim() || createMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {createMutation.isPending ? <Spinner className="size-4 text-white" /> : <Check className="size-4" />}
              생성
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
